import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import i18next from 'i18next';

import startCase from 'lodash.startcase';
import Status from './Status';
import { transactionTypesArray } from '../../../util/enum/api/transactionTypes';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import Tooltip from '../../Tooltip/Tooltip';
import useWindowSize from '../../../util/hooks/useIsMobileHook';
import { mediaBelow, pxToRem, pxToRemMd } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import { uFlexColumn } from '../../../assets/styles/utility';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import Button from '../../Button/Button';
import { pendingTransactionCancel } from '../../Table/Components/PendingTransaction';
import useCheckPermissions from '../../../util/hooks/useCheckPermissions';

const PendingTransactionCardContainer = styled.div`
  padding: ${pxToRem(18)} ${pxToRem(24)};
  cursor: pointer;
  display: grid;
  grid-template-columns: 1fr ${pxToRem(162)} ${pxToRem(80)};
  grid-column-gap: ${pxToRem(24)};
  align-items: center;

  &:nth-child(even) {
    background-color: ${themeColors.colorTableEvenRowBackground};
  }

  &:hover {
    background-color: ${themeColors.colorTableEvenRowHoverBackground};
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding: ${pxToRemMd(22)} ${pxToRemMd(12)} ${pxToRemMd(22)} ${pxToRemMd(20)};
  }

  ${mediaBelow(variables.breakpoints.bpSm)} {
    grid-template-columns: 1fr auto ${pxToRemMd(16)};
  }
`;

const PendingTransactionCardInfo = styled.div`
  display: flex;
  align-items: center;
`;

const PendingTransactionCardDate = styled.div`
  ${uFlexColumn};
  align-items: center;
  justify-content: center;
  color: ${themeColors.colorTextSecondary};
  margin-right: ${pxToRem(24)};
  font-weight: 600;
  flex-shrink: 0;
`;

const PendingTransactionCardMonth = styled.p`
  text-align: center;
  font-size: ${pxToRem(14)};
  line-height: 1.6;
  letter-spacing: 0;
  text-transform: uppercase;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: ${pxToRemMd(14)};
    line-height: 1.72;
  }
`;

const PendingTransactionCardDay = styled.p`
  text-align: center;
  font-size: ${pxToRem(14)};
  line-height: 1.6;
  letter-spacing: 0;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: ${pxToRemMd(14)};
    line-height: 1.72;
  }
`;

const PendingTransactionCardTitle = styled.div`
  ${uFlexColumn};
  width: 100%;
`;

const PendingTransactionCardAction = styled.p`
  text-align: left;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    line-height: 1.5;
  }
`;

const PendingTransactionCardStatus = styled.div`
  display: flex;
  justify-content: flex-start;

  ${mediaBelow(variables.breakpoints.bpSm)} {
    margin-right: ${pxToRemMd(12)};
    flex-shrink: 0;
    min-width: 0;
  }
`;

const PendingTransactionCardIcon = styled.div`
  flex-shirk: 0;

  button {
    min-width: ${pxToRem(80)};
  }
`;

const PendingTransactionCard = ({
  month,
  day,
  amount,
  status,
  action,
  product,
  accountUid,
  paymentUid,
  canCancel,
  handleModalOpen,
}) => {
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < 576;
  const { allowAccountChangesPermission } = useCheckPermissions();

  const transactionTypeConst = parseEnumType(transactionTypesArray, action);
  const transactionType = amount
    ? i18next.t(
        `portfolio.transactions.portfolioTypes.${transactionTypeConst}`,
        {
          amount: formatMoneyNumeral(amount),
        },
      )
    : i18next.t(
        `portfolio.transactions.portfolioTypesNonAmount.${transactionTypeConst}`,
      );

  return (
    <PendingTransactionCardContainer data-cy="list-item-pending-transaction">
      <PendingTransactionCardInfo>
        <PendingTransactionCardDate>
          <PendingTransactionCardMonth>
            {month.substring(0, 3)}
          </PendingTransactionCardMonth>
          <PendingTransactionCardDay>{day}</PendingTransactionCardDay>
        </PendingTransactionCardDate>
        <PendingTransactionCardTitle>
          {(action || product) && (
            <PendingTransactionCardAction>
              {action && transactionType}
            </PendingTransactionCardAction>
          )}
        </PendingTransactionCardTitle>
      </PendingTransactionCardInfo>
      <PendingTransactionCardStatus>
        {isMobile ? (
          <Tooltip text={startCase(status)}>
            <Status status={status} />
          </Tooltip>
        ) : (
          <Status status={status} />
        )}
      </PendingTransactionCardStatus>
      {canCancel && (
        <PendingTransactionCardIcon>
          <Button
            css={pendingTransactionCancel}
            onClick={() => handleModalOpen(accountUid, paymentUid)}
            disabled={!canCancel || !allowAccountChangesPermission}
            variant="primaryOutlined"
            size="sm"
          >
            {i18next.t('common.cancel')}
          </Button>
        </PendingTransactionCardIcon>
      )}
    </PendingTransactionCardContainer>
  );
};

PendingTransactionCard.propTypes = {
  month: PropTypes.string,
  day: PropTypes.string,
  amount: PropTypes.number,
  status: PropTypes.string,
  action: PropTypes.number,
  product: PropTypes.string,
  accountUid: PropTypes.string,
  paymentUid: PropTypes.string,
  handleModalOpen: PropTypes.func,
  canCancel: PropTypes.bool,
};

export default PendingTransactionCard;
